import React from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import './PreviewPrime.css';

// Format text by replacing underscores with spaces and capitalizing first letter of each word
const formatText = text => {
  if (!text) return '';

  // Replace underscores with spaces
  const textWithSpaces = text.replace(/_/g, ' ');

  // Capitalize the first letter of each word
  return textWithSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PreviewPrimeSchedule = ({ open, onClose, session }) => {
  // Get prime session data directly if not provided
  const primeSession = session;
  console.log('Prime session data:', primeSession);

  if (!primeSession) return null;

  // Use tags from the session data or an empty array if not available
  const tags = primeSession.tags || [];

  // Transform availability data from overrides if available
  const transformAvailability = () => {
    // Check if overrides are available and use them if they are
    if (primeSession.overrides && primeSession.overrides.length > 0) {
      return primeSession.overrides.map(override => {
        // Extract day name from date (e.g., "2025-05-19" -> "Monday")
        const dayDate = new Date(override.date);
        const daysOfWeek = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
        const dayOfWeek = daysOfWeek[dayDate.getDay()];

        return {
          date: override.date,
          day_of_week: dayOfWeek,
          time_slots: [
            {
              start_time: override.startTime,
              end_time: override.endTime,
            },
          ],
        };
      });
    }

    // Fall back to original availability implementation if overrides aren't available
    if (!primeSession.availability) return [];

    // Check if the availability is in the API format (with days array)
    const isApiFormat =
      primeSession.availability.length > 0 && Array.isArray(primeSession.availability[0].days);

    if (isApiFormat) {
      // Transform API format to the expected format for prime sessions
      // For prime sessions, we need to convert days-based format to dates
      const transformed = [];

      // Create a date-based entry for each day in each availability slot
      primeSession.availability.forEach(slot => {
        slot.days.forEach(day => {
          // For demo purposes, create a date for this day (assuming current week)
          const date = getDateForDay(day);

          transformed.push({
            date: date.toISOString().split('T')[0], // Format as YYYY-MM-DD
            day_of_week: day,
            time_slots: [
              {
                start_time: slot.startTime,
                end_time: slot.endTime,
              },
            ],
          });
        });
      });

      return transformed;
    }

    // Return as is if already in the expected format
    return primeSession.availability;
  };

  // Helper function to get a Date object for a given day of the week
  const getDateForDay = dayName => {
    const days = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 0,
    };

    const today = new Date();
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const targetDayOfWeek = days[dayName];

    // Calculate the difference in days
    let diff = targetDayOfWeek - currentDayOfWeek;
    if (diff < 0) diff += 7; // If in the past, go to next week

    // Create a new date by adding the difference
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + diff);

    return targetDate;
  };

  const availability = transformAvailability();

  // Format the level of teaching
  const formattedLevel =
    primeSession.level_of_teaching === 'all'
      ? 'All Levels'
      : formatText(primeSession.level_of_teaching || primeSession.level || 'Not specified');

  // Group availability by week - modified to show first 3 days in Week 1 and the rest in Week 2
  const groupByWeek = () => {
    if (!availability || availability.length === 0) {
      return [];
    }

    // Sort availability by date
    const sortedAvailability = [...availability].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });

    // Create two weeks - first 3 dates in Week 1, rest in Week 2
    const weeks = [];

    // First 3 days go to Week 1
    const week1 = sortedAvailability.slice(0, 3);
    if (week1.length > 0) {
      weeks.push(week1);
    }

    // Remaining days go to Week 2
    const week2 = sortedAvailability.slice(3);
    if (week2.length > 0) {
      weeks.push(week2);
    }

    return weeks;
  };

  const weeks = groupByWeek();

  // Get unique time slot - assuming all sessions have the same time
  const getTimeSlot = () => {
    // If there are overrides, use the time from the first override
    if (primeSession.overrides && primeSession.overrides.length > 0) {
      const firstOverride = primeSession.overrides[0];
      return {
        start_time: firstOverride.startTime,
        end_time: firstOverride.endTime,
      };
    }

    // Fall back to original implementation
    if (
      !availability ||
      availability.length === 0 ||
      !availability[0].time_slots ||
      availability[0].time_slots.length === 0
    ) {
      return { start_time: '', end_time: '' };
    }

    return availability[0].time_slots[0];
  };

  const timeSlot = getTimeSlot();

  // Get day abbreviation
  const getDayAbbr = dayOfWeek => {
    const days = {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    };
    return days[dayOfWeek] || dayOfWeek;
  };

  // Get date from date string
  const getDateNumber = dateString => {
    return new Date(dateString).getDate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-modal-title"
      className="preview-modal"
    >
      <Box className="preview-modal-content">
        <Box className="preview-modal-header">
          <Typography variant="h6" className="preview-title">
            Preview
          </Typography>
          <IconButton onClick={onClose} className="close-button" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="preview-modal-body">
          <Box className="preview-section">
            <Typography className="section-title">Subject</Typography>
            <Box className="section-content-prime">
              <Typography className="section-value">
                {primeSession.subject === 'all'
                  ? 'All'
                  : formatText(primeSession.subject || 'Not provided')}
              </Typography>
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Tags</Typography>
            <Box className="tags-content-prime prime-tags-container">
              {tags.length > 0 ? (
                tags.map((tag, index) => (
                  <Box key={index} className="prime-tag">
                    <Typography className="prime-tag-text">{tag}</Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Level to teach</Typography>
            <Box className="section-content-prime">
              <Typography className="section-value">{formattedLevel}</Typography>
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Session Type</Typography>
            <Box className="section-content-prime">
              <Typography className="prime-text">Prime Sessions</Typography>
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Dates</Typography>
            <Box className="section-content-prime direction dates-content">
              {weeks.length > 0 ? (
                weeks.map((week, weekIndex) => (
                  <Box key={weekIndex} className="week-container">
                    <Typography className="week-label">
                      Week {String(weekIndex + 1).padStart(2, '0')}
                    </Typography>
                    <Box className="date-circles-container">
                      {week.map((day, dayIndex) => (
                        <Box key={dayIndex} className="date-circle-wrapper">
                          <Box className="date-circle">
                            <Typography className="date-number">
                              {getDateNumber(day.date)}
                            </Typography>
                            <Typography className="day-abbr">
                              {getDayAbbr(day.day_of_week)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  No scheduled dates available
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Timings</Typography>
            <Box className="section-content-prime time-content">
              <Box className="time-slot-display">
                <Typography className="section-value">
                  {`${formatTime(timeSlot.start_time)} - ${formatTime(timeSlot.end_time)}`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className="preview-section">
            <Typography className="section-title">Descriptions</Typography>
            <Box className="section-content-prime">
              <Typography className="description-text">
                {primeSession.description || 'No description provided'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Helper function to format time
const formatTime = time => {
  if (!time) return '';

  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);

  let formattedHour = hour;
  let period = 'AM';

  if (hour >= 12) {
    period = 'PM';
    if (hour > 12) {
      formattedHour = hour - 12;
    }
  }

  if (hour === 0) {
    formattedHour = 12;
  }

  return `${formattedHour}:${minutes} ${period}`;
};

export default PreviewPrimeSchedule;
